import { newEvent } from '@default/scripts/modules/helpers';
import { articleListMarkupHP } from './recombee/articleListMarkupHP';
import { articleListMarkup as articleListMarkupDef } from './recombee/articleListMarkup';
import { pad } from './helpers';
import systemIdForDomain from './gtm-system-id-for-domain.json';
import { EVENT_AFTER_LOADEDARTICLES } from './recombee/config';

const recombeeSourceData = window.CNC.recombee;
const { recombee: recombeeFunctions, dataLayer } = window;

// Inicializace - definováno v hlavičce.
const { client, userId, articleId: sourceArticleId } = recombeeSourceData;

// ID výpisů ostatních článků na stránce.
const otherArticlesIds = [...document.querySelectorAll('[data-article-id]')].reduce(
    (articlesIdsArray, article) => {
        const { articleId } = article.dataset;
        if (!articlesIdsArray.includes(articleId)) articlesIdsArray.push(articleId);
        return articlesIdsArray;
    },
    [],
);

/**
 * @name getLoadMoreError
 * @description Vrací HTML element s textem chyby u tlačítka pro načtení dalších článků.
 *
 * @param {HTMLElement} contextElement
 * @returns {HTMLElement}
 */
const getLoadMoreError = (contextElement) =>
    contextElement.querySelector('[data-load-more-error="rec"]');

/**
 * @name handleError
 * @description Vypisuje text chyby do konzole a zobrazí chybovou hlášku nad tlačítkem pro načtení dalších článků.
 *
 * @param {string} err Text chybové zprávy.
 * @param {HTMLElement} contextElement
 */
function handleError(err, contextElement) {
    // Výpis chyby do konzole pro ladění. Zobrazuje se jen při chybě, proto vyjmuto z ESLintu.
    console.log('Could not load recomms: ', err); // eslint-disable-line no-console
    const loadMoreError = getLoadMoreError(contextElement);

    if (loadMoreError) {
        loadMoreError.removeAttribute('hidden');
    }
}

const articleListMarkup = (settings, ...props) => {
    const { scenario } = settings;
    return [
        'blesk-article-odjinud',
        'blesk-article-doporucujeme-celebrity',
        'blesk-article-doporucujeme-zpravy',
    ].includes(scenario)
        ? articleListMarkupDef(settings, ...props)
        : articleListMarkupHP(...props);
};

/**
 * @name renderArticles
 * @description Nahrazuje články ve výpisu články z doporučení recombee, podle daného scénáře.
 * Umožňuje načítání dalších článků z recombee pomocí tlačítka.
 *
 * @param {NodeListOf<HTMLElement>} elements Pole s celými výpisy článků.
 */
export const renderArticles = (elements) => {
    if (!recombeeSourceData.client) return;

    /* GTM 2.0 - dataLayer push "page" */
    if (typeof dataLayer === 'object') {
        dataLayer.push({
            page: {
                pagination: {
                    type: 'more',
                    page: 1,
                },
            },
        });
    }

    const loadRecommbeData = () => {
        elements.forEach((element, elementIndex) => {
            // Přeskočit všechny již načtené nebo nenačtené boxy, aktuální ve viewportu má stav loading
            if (
                element.dataset.recombeeLoaded === 'unloaded' ||
                element.dataset.recombeeLoaded === 'loaded'
            )
                return;

            const recombeeBoxSourceData = recombeeSourceData.box[elementIndex];
            const articlesList = element.querySelectorAll('.articles__item');
            const articlesCount = articlesList.length;

            // Inicializace - definováno v boxu s elementem.
            const {
                list: listName = 'articleList',
                scenario = 'newreflex-odjinud',
                endpoint = 'items-to-user',
                articleTypeClass = 'articles__item',
                articlePreviewClass = 'article-preview',
                customReplace = '',
                titleLength = 80,
                skippedPositions = [],
            } = recombeeBoxSourceData;

            const areCrossArticles = scenario.indexOf('-odjinud') > 0;

            const replaceCategory = customReplace === 'replace-category-magazine';

            let recommId;
            let articleIndex = 1;

            /**
             * @name loadedArticles
             * @description Vytvoří HTML strukturu článků a vykreslí je do DOM.
             *
             * @param {object} resp Data pro výpis článků.
             * @param {Boolean} replace True nahrazuje články standardního výpisu, false načítá nové před tlačítko "Načíst další".
             */
            function loadedArticles(resp, replace) {
                ({ recommId } = resp);
                const { recomms } = resp;

                recomms.forEach((article, index) => {
                    // Přeskoč nahrazení článku za článek z Recombee pokud je definovaná pozice v nastaveni boxu
                    if (
                        skippedPositions.findIndex((skippedIndex) => skippedIndex === index) !== -1
                    ) {
                        return;
                    }

                    const recombeeQueryKey = 'recombee_recomm_id=';
                    const currentValues = article.values;
                    const isPremium = currentValues.locked || currentValues.is_premium;
                    const publishedAt = new Date(currentValues.published_at * 1000); // V milisekundách.
                    const monthsTranslates = [
                        'ledna',
                        'února',
                        'března',
                        'dubna',
                        'května',
                        'června',
                        'července',
                        'srpna',
                        'září',
                        'října',
                        'listopadu',
                        'prosince',
                    ];

                    const publishedDate = `<span>${publishedAt.getDate()}. ${
                        monthsTranslates[publishedAt.getMonth()]
                    } ${publishedAt.getFullYear()}</span><span>&#x2022; ${pad(
                        publishedAt.getHours(),
                    )}:${pad(publishedAt.getMinutes())}</span>`;

                    const trackingData = {
                        id: article.id,
                        index: index + articleIndex,
                        list: listName,
                        systemId: systemIdForDomain[currentValues.domain.replace('www.', '')],
                        source: areCrossArticles
                            ? `crossSiteRecommended-${scenario}`
                            : `siteRecommended-${scenario}`,
                    };

                    const loadedArticle = articleListMarkup(
                        { scenario, titleLength: +titleLength },
                        articleTypeClass,
                        articlePreviewClass,
                        currentValues.title,
                        currentValues.url +
                            (String(currentValues.url).includes('?') ? '&' : '?') +
                            recombeeQueryKey +
                            recommId,
                        currentValues.image,
                        publishedDate,
                        isPremium,
                        currentValues.author,
                        currentValues.categories,
                        trackingData,
                        replaceCategory
                            ? currentValues.magazine_name
                            : currentValues.main_category_name,
                        currentValues.main_category_urlize,
                    );

                    if (replace) {
                        articlesList[index].insertAdjacentHTML('beforebegin', loadedArticle);
                        articlesList[index].remove();
                    } else {
                        element
                            .querySelector('.list-article--vertical__load-more')
                            .insertAdjacentHTML('beforebegin', loadedArticle);
                    }
                });

                if (window.gtmService && window.gtmService.observe instanceof Function) {
                    window.gtmService.observe();
                }

                articleIndex += recomms.length;
                const loadMoreError = getLoadMoreError(element);

                if (loadMoreError) {
                    loadMoreError.setAttribute('hidden', '');
                }
            }

            let filter = "'title' != null";

            if (element.dataset.recombee === 'articles-tabheader') {
                filter += ` and 'itemId' not in ${JSON.stringify(otherArticlesIds)}`;
            }

            const recombeeRequestConfig = {
                returnProperties: true,
                distinctRecomms: true, // Zamezení duplicit.
                includedProperties: [
                    'title',
                    'url',
                    'image',
                    'published_at',
                    'author',
                    'categories',
                    'locked',
                    'is_premium',
                    'updated_at',
                    'domain',
                    'main_category_name',
                    'main_category_urlize',
                    'magazine_name',
                ],
                filter,
                scenario,
            };

            let recombeeRequest;

            if (endpoint === 'items-to-item') {
                recombeeRequest = new recombeeFunctions.RecommendItemsToItem(
                    sourceArticleId,
                    userId,
                    articlesCount,
                    recombeeRequestConfig,
                );
            } else {
                recombeeRequest = new recombeeFunctions.RecommendItemsToUser(
                    userId,
                    articlesCount,
                    recombeeRequestConfig,
                );
            }

            // Požadavek na doporučené články.
            client.send(recombeeRequest, (err, resp) => {
                if (err) {
                    handleError(err, element);
                    return;
                }

                const buttonLoadMoreRendered = element.querySelector(
                    '.list-article--vertical__load-more',
                );

                // Odstraní tlačítko pro načtení dalších článků, pokud ho vykreslí šablona.
                if (buttonLoadMoreRendered) {
                    buttonLoadMoreRendered.remove();
                }

                loadedArticles(resp, true);
                element.dispatchEvent(
                    newEvent(EVENT_AFTER_LOADEDARTICLES, { detail: { scenario } }),
                );
            });
        });
    };

    // Nastavení pro Recombee Observer
    const RecombeeIntersectionOptions = {
        root: null,
        rootMargin: '500px',
        threshold: 0,
    };

    // Recombee Observer správa položek
    const handleRecombeeIntersect = (entries, RecombeeIntersectionObserver) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                // Nastavení data-recombee-loaded
                entry.target.dataset.recombeeLoaded = 'loading';
                // Načíst data pro box který se dostaly do viewportu
                loadRecommbeData();
                entry.target.dataset.recombeeLoaded = 'loaded';
                // Kvuli specialnimu layoutu na HP zmen display na contents
                entry.target.classList.add('recombee--contents');
                // Přestat box sledovat
                RecombeeIntersectionObserver.unobserve(entry.target);
            }
        });
    };

    // Recombee Observer
    const RecombeeIntersectionObserver = new IntersectionObserver(
        handleRecombeeIntersect,
        RecombeeIntersectionOptions,
    );

    // Pro všechny seznamy článků s 'data-recombee' zapni Observer
    elements.forEach((element) => {
        RecombeeIntersectionObserver.observe(element);
    });
};
